import { useState } from 'react'
import { Edit2, Plus, Target, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  useAIAgentMissions,
  useCreateMission,
  useDeleteMission,
  useUpdateMission,
  type AIAgentMission,
} from '@/api/aiAgents'

interface Props {
  /** Dans une ligne repliable : le titre est déjà affiché par le parent. */
  sansEntete?: boolean
  companyId: number | undefined
  agentId: number | undefined
  /**
   * Un calendrier (Google ou Microsoft) est-il lié à l'agent ?
   * Sans lui, les missions rendez-vous ordonneraient au modèle des créneaux
   * et des réservations qu'il ne peut qu'inventer — leur interrupteur est
   * donc verrouillé (le backend refuse aussi, `calendar_required`).
   */
  hasCalendar?: boolean
}

/** Missions dont l'activation exige un calendrier lié (miroir du backend). */
const CALENDAR_MISSION_KINDS = new Set(['book_appointment', 'confirm_appointment'])

/**
 * Variables employées dans les textes de la mission, déduites en silence.
 *
 * La notion de variable n'est plus exposée : pour adapter une mission à une
 * branche, le client écrit une phrase dans le nœud du flux (« Marc est
 * absent cette semaine ») et le modèle en tient compte. Le champ
 * `variables` continue d'être calculé et envoyé, car la substitution
 * littérale reste utile côté serveur pour les missions déjà écrites avec
 * des {{placeholders}}, et le jour où une valeur viendra d'une donnée
 * d'appel qui devra être répétée mot pour mot.
 */
function extractVariables(...texts: string[]): string[] {
  const found = new Set<string>()
  for (const text of texts) {
    for (const match of (text || '').matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
      found.add(match[1])
    }
  }
  return [...found]
}

/**
 * Section « Missions » de l'onglet Actions — sous-conversations cadrées que
 * l'agent exécute quand une branche du flux d'appels (nœud « Agent IA ») ou
 * un appel de test la lui confie. Sauvegarde directe via l'API (indépendante
 * du formulaire agent) : une mission créée est immédiatement invocable.
 */
export function MissionsSection({ companyId, agentId, hasCalendar = false, sansEntete = false }: Props) {
  const { t } = useTranslation()
  const { data: missions = [] } = useAIAgentMissions(companyId, agentId)
  const createMission = useCreateMission(companyId, agentId)
  const updateMission = useUpdateMission(companyId, agentId)
  const deleteMission = useDeleteMission(companyId, agentId)

  // 'new' = création ; objet = édition ; null = fermé
  const [editing, setEditing] = useState<AIAgentMission | 'new' | null>(null)
  const [name, setName] = useState('')
  const [instructions, setInstructions] = useState('')
  const [firstMessage, setFirstMessage] = useState('')
  const [isActive, setIsActive] = useState(true)

  const openEditor = (mission: AIAgentMission | 'new') => {
    if (mission === 'new') {
      setName('')
      setInstructions('')
      setFirstMessage('')
      setIsActive(true)
    } else {
      setName(mission.name)
      setInstructions(mission.instructions)
      setFirstMessage(mission.first_message ?? '')
      setIsActive(mission.is_active)
    }
    setEditing(mission)
  }

  const handleSave = async () => {
    const variables = extractVariables(instructions, firstMessage)
    try {
      if (editing === 'new') {
        await createMission.mutateAsync({
          name: name.trim(),
          instructions: instructions.trim(),
          first_message: firstMessage.trim(),
          variables,
          is_active: isActive,
        })
      } else if (editing) {
        await updateMission.mutateAsync({
          missionId: editing.id,
          name: name.trim(),
          instructions: instructions.trim(),
          first_message: firstMessage.trim(),
          variables,
          is_active: isActive,
        })
      }
      setEditing(null)
    } catch {
      toast.error(t('manager.aiAgents.missions.saveError'))
    }
  }

  const handleDelete = async (mission: AIAgentMission) => {
    try {
      await deleteMission.mutateAsync(mission.id)
    } catch {
      toast.error(t('manager.aiAgents.missions.deleteError'))
    }
  }

  const isPending = createMission.isPending || updateMission.isPending

  return (
    <section>
      <div className={sansEntete ? 'flex justify-end mb-3' : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'}>
        {!sansEntete && (
        <div>
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
            {t('manager.aiAgents.missions.title')}
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mt-1">
            {t('manager.aiAgents.missions.description')}
          </p>
        </div>
        )}
        <Button
          type="button"
          size="sm"
          onClick={() => openEditor('new')}
          disabled={!agentId}
          className="gap-2 w-full sm:w-auto text-sm"
        >
          <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          {t('manager.aiAgents.missions.add')}
        </Button>
      </div>

      {!agentId && (
        <p className="text-xs text-gray-500 dark:text-slate-400 italic">
          {t('manager.aiAgents.missions.saveAgentFirst')}
        </p>
      )}

      {agentId && missions.length > 0 && (
        <div className="border dark:border-slate-800 rounded-lg overflow-hidden bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
          {missions.map((mission) => {
            // Verrou calendrier : on ne peut pas ACTIVER une mission
            // rendez-vous sans calendrier lié. La désactiver reste permis
            // (cas d'un calendrier délié après coup).
            const needsCalendar =
              CALENDAR_MISSION_KINDS.has(mission.kind) && !hasCalendar
            const activationLocked = needsCalendar && !mission.is_active
            return (
            <div
              key={mission.id}
              className="group flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                    {mission.name}
                  </span>
                  {!mission.is_active && (
                    <Badge variant="secondary" className="text-[10px]">
                      {t('manager.aiAgents.missions.inactive')}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                  {needsCalendar
                    ? t('manager.aiAgents.missions.calendarRequired')
                    : mission.instructions}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => openEditor(mission)}
                  >
                    <Edit2 className="h-3.5 w-3.5 text-gray-500" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => void handleDelete(mission)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
                {/* Interrupteur toujours visible : les missions par défaut se
                    lisent comme des capacités à activer, pas à rédiger. */}
                <Switch
                  checked={mission.is_active}
                  disabled={activationLocked}
                  title={
                    activationLocked
                      ? t('manager.aiAgents.missions.calendarRequired')
                      : undefined
                  }
                  onCheckedChange={(checked) =>
                    void updateMission
                      .mutateAsync({ missionId: mission.id, is_active: checked })
                      .catch(() => toast.error(t('manager.aiAgents.missions.saveError')))
                  }
                />
              </div>
            </div>
            )
          })}
        </div>
      )}

      {/* Éditeur de mission */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-600" />
              {editing === 'new'
                ? t('manager.aiAgents.missions.createTitle')
                : t('manager.aiAgents.missions.editTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mission-name">
                {t('manager.aiAgents.missions.nameLabel')}
              </Label>
              <Input
                id="mission-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('manager.aiAgents.missions.namePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mission-instructions">
                {t('manager.aiAgents.missions.instructionsLabel')}
              </Label>
              <Textarea
                id="mission-instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={6}
                placeholder={t('manager.aiAgents.missions.instructionsPlaceholder')}
              />
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {t('manager.aiAgents.missions.instructionsHint')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mission-first-message">
                {t('manager.aiAgents.missions.firstMessageLabel')}
              </Label>
              <Textarea
                id="mission-first-message"
                value={firstMessage}
                onChange={(e) => setFirstMessage(e.target.value)}
                rows={2}
                placeholder={t('manager.aiAgents.missions.firstMessagePlaceholder')}
              />
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {t('manager.aiAgents.missions.firstMessageHint')}
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border dark:border-slate-800 px-4 py-3">
              <Label htmlFor="mission-active" className="text-sm">
                {t('manager.aiAgents.missions.activeLabel')}
              </Label>
              <Switch
                id="mission-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(null)}
                className="flex-1"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => void handleSave()}
                disabled={isPending || !name.trim() || !instructions.trim()}
                className="flex-1"
              >
                {editing === 'new'
                  ? t('manager.aiAgents.missions.create')
                  : t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
